import {
  TrendingDown,
  ShieldCheck,
  CalendarClock,
  ArrowRightLeft,
  Sparkles,
  CheckCircle2,
  Clock,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import type { Module } from '@/types'

const ICON_MAP: Record<string, React.ReactNode> = {
  TrendingDown: <TrendingDown size={20} />,
  ShieldCheck: <ShieldCheck size={20} />,
  CalendarClock: <CalendarClock size={20} />,
  ArrowRightLeft: <ArrowRightLeft size={20} />,
  Sparkles: <Sparkles size={20} />,
}

interface ModuleCardProps {
  module: Module
  isComplete: boolean
  onClick: () => void
}

export function ModuleCard({ module, isComplete, onClick }: ModuleCardProps) {
  return (
    <Card
      padding={false}
      className={cn(
        'cursor-pointer hover:shadow-[var(--shadow-md)] transition-all duration-200 hover:-translate-y-0.5 group',
        isComplete && 'border-emerald-200 dark:border-emerald-800',
      )}
      onClick={onClick}
    >
      <div className="p-5">
        {/* Header row */}
        <div className="flex items-start gap-3 mb-3">
          <div
            className={cn(
              'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors',
              isComplete
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400'
                : 'bg-[var(--accent-blue-light)] text-[var(--accent-blue)]',
            )}
          >
            {ICON_MAP[module.icon] ?? <TrendingDown size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs text-[var(--text-muted)]">Method {module.number}</span>
              {isComplete && (
                <Badge variant="green">
                  <CheckCircle2 size={10} />
                  Complete
                </Badge>
              )}
            </div>
            <h3 className="font-semibold text-[var(--text-primary)] leading-tight">
              {module.title}
            </h3>
          </div>
          <ChevronRight
            size={16}
            className="flex-shrink-0 text-[var(--text-muted)] group-hover:text-[var(--accent-blue)] transition-colors mt-1"
          />
        </div>

        {/* Tagline */}
        <p className="text-sm text-[var(--text-secondary)] line-clamp-2 mb-3">
          {module.tagline}
        </p>

        {/* Footer */}
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
            <Clock size={12} />
            {module.estimatedMinutes} min
          </span>
          <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
            {module.steps.length} steps
          </span>
        </div>
      </div>

      {/* Progress indicator bar */}
      <div
        className={cn(
          'h-1 rounded-b-xl transition-all duration-300',
          isComplete ? 'bg-emerald-500' : 'bg-[var(--border)]',
        )}
      />
    </Card>
  )
}
